import React from "react";
import { useTranslation } from "react-i18next";
import { useProjectResourcesRetrieval } from "@planzone/view-models";
import { Identifiable, Resource as R } from "@planzone/dto";
import { Props } from "./types";
import { Section } from "./section";
import { Resource } from "./resource";
import { Menu } from "./menu";
import styles from "./styles.scss";

function getHumanResources(resources: Identifiable<R>[]): Identifiable<R>[] {
  return resources.filter(({ type }) => type === "HUMAN");
}

function getMaterialResources(resources: Identifiable<R>[]): Identifiable<R>[] {
  return resources.filter(({ type }) => type === "MATERIAL");
}

function getVirtualResources(resources: Identifiable<R>[]): Identifiable<R>[] {
  return resources.filter(({ type }) => type === "VIRTUAL");
}

function Resources({ project }: Props) {
  const { t } = useTranslation();
  const {
    isRequestPending,
    isRequestSuccess,
    isRequestFailure,
    projectResources,
    retrieveProjectResources,
  } = useProjectResourcesRetrieval(project);
  const resources = projectResources.map(({ resource }) => resource);
  const humanResources = getHumanResources(resources);
  const materialResources = getMaterialResources(resources);
  const virtualResources = getVirtualResources(resources);

  React.useEffect(() => {
    !!project.uuid &&
      !isRequestPending &&
      !isRequestSuccess &&
      !isRequestFailure &&
      retrieveProjectResources(project);
  }, [project.uuid]);

  return (
    <div className={styles.resources}>
      <div className={styles.header}>
        <h3 className={styles.title}>{t("projectDetails.resources.title")}</h3>
        <Menu project={project} />
      </div>
      <div className={styles.main}>
        {humanResources.length > 0 && (
          <Section title={t("projectDetails.resources.humanResources")}>
            <div className={styles.list}>
              {humanResources.map((resource) => (
                <Resource key={resource.uuid} resource={resource} />
              ))}
            </div>
          </Section>
        )}
        {virtualResources.length > 0 && (
          <Section title={t("projectDetails.resources.virtualResources")}>
            <div className={styles.list}>
              {virtualResources.map((resource) => (
                <Resource key={resource.uuid} resource={resource} />
              ))}
            </div>
          </Section>
        )}
        {materialResources.length > 0 && (
          <Section title={t("projectDetails.resources.materialResources")}>
            <div className={styles.list}>
              {materialResources.map((resource) => (
                <Resource key={resource.uuid} resource={resource} />
              ))}
            </div>
          </Section>
        )}
      </div>
    </div>
  );
}

export { Resources };
